import React, { useEffect, useRef, useState } from "react";

import "./EmailController.scss";
import EmailTemplate from "./EmailTemplate/EmailTemplate.tsx";
import EmailSent from "./EmailSent/EmailSent.tsx";
import EmailPreview from "./EmailPreview/EmailPreview.tsx";
import { EmailAttachments } from "./EmailAttachments/EmailAttachments.tsx";
import SendEmailButton from "./SendEmailButton/SendEmailButton.tsx";
import { IconFolder, IconSparkles } from "@tabler/icons-react";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";
import {
  createName,
  getTaxProEmailInfo,
} from "@components/Tax Pro Only/Modals/EmailModal/utils.ts";
import { ChoiceButton } from "@components/Tax Pro Only/ChoiceButton/ChoiceButton.tsx";
import "@components/Tax Pro Only/ChoiceButton/ChoiceButton.scss";
import EmailGenerator from "@components/Tax Pro Only/Modals/EmailModal/EmailController/EmailGenerator/EmailGenerator.tsx";
import type { EmailTab } from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import { SENT_COMPONENT_TIMEOUT } from "@components/Tax Pro Only/Modals/EmailModal/constants.ts";

interface EmailControllerProps {
  onEmailSent: () => void;
}

const EmailController: React.FC<EmailControllerProps> = ({ onEmailSent }) => {
  const [emailTab, setEmailTab] = useState<EmailTab>("jaz");
  const { setBusinessInfo, hasSent, resetState } = useEmailStore();
  const hasNotifiedRef = useRef(false);

  // Loads tax pro information on startup.
  useEffect(() => {
    const { personalInfo, businessInfo } = getTaxProEmailInfo();
    if (businessInfo && personalInfo) {
      setBusinessInfo({
        businessName: businessInfo.businessName,
        name: createName(personalInfo?.firstName, personalInfo?.lastName),
      });
    }
  }, [setBusinessInfo]);

  // When email is sent, resets the state of the modal.
  useEffect(() => {
    if (hasSent && !hasNotifiedRef.current) {
      onEmailSent();
      hasNotifiedRef.current = true;

      setTimeout(() => {
        resetState();
      }, SENT_COMPONENT_TIMEOUT);
    }
  }, [hasSent, resetState, onEmailSent]);

  if (hasSent) return <EmailSent />;

  return (
    <div className="email-generator-container">
      <div className="form-wrapper">
        <div className="content-wrapper">
          <div className="email-generator-form">
            <h3 className="email-generator-form-title">Method</h3>
            <div className="email-generator-form-buttons">
              <ChoiceButton
                label="Jaz"
                dataTestId="jaz-button"
                icon={<IconSparkles size={24} />}
                checked={emailTab === "jaz"}
                onClick={() => setEmailTab("jaz")}
              />
              <ChoiceButton
                label="Templates"
                className="choice-btn"
                dataTestId="templates-button"
                icon={<IconFolder size={24} />}
                checked={emailTab === "templates"}
                onClick={() => setEmailTab("templates")}
              />
            </div>

            {emailTab === "jaz" && (
              <div className="tab-panel-jaz">
                <EmailGenerator />
              </div>
            )}

            {emailTab === "templates" && (
              <div
                className={`tab-panel-templates ${
                  emailTab === "templates" ? "active" : ""
                }`}
              >
                <EmailTemplate />
              </div>
            )}
          </div>

          <div className="email-generator-view">
            <EmailPreview />
            <EmailAttachments />
          </div>
        </div>
        <span className="send-email-button-container">
          <SendEmailButton />
        </span>
      </div>
    </div>
  );
};

export default EmailController;
